import { formatCurrency } from '@theme/utilities';

class CartProductRecommendations extends HTMLElement {
  /**
   * The cached recommendations for different product combinations
   * @type {Record<string, Object>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Track if we're currently loading
   * @type {boolean}
   */
  #isLoading = false;

  /**
   * Track if recommendations have been loaded at least once
   * @type {boolean}
   */
  #hasLoaded = false;

  /**
   * Store the last cart state to detect actual changes
   * @type {string | null}
   */
  #lastCartState = null;

  /**
   * Retry count for failed loads
   * @type {number}
   */
  #retryCount = 0;

  /**
   * Max retries
   * @type {number}
   */
  #maxRetries = 2;

  /**
   * MutationObserver to watch for grid changes
   * @type {MutationObserver | null}
   */
  #gridObserver = null;

  /**
   * Cached recommendations to re-render after morphing
   * @type {Array | null}
   */
  #lastRenderedProducts = null;

  /**
   * Cached cart IDs to re-render after morphing
   * @type {Array | null}
   */
  #lastRenderedCartIds = null;

  connectedCallback() {
    // Listen for cart updates to refresh recommendations
    document.addEventListener('cart:update', this.#handleCartUpdate);

    // Watch for when the grid gets replaced by morphing
    this.#setupGridObserver();

    // Use requestAnimationFrame to ensure DOM is fully settled after morphing
    requestAnimationFrame(() => {
      this.#loadRecommendations();
    });
  }

  disconnectedCallback() {
    if (this.#gridObserver) {
      this.#gridObserver.disconnect();
    }
    document.removeEventListener('cart:update', this.#handleCartUpdate);
  }

  /**
   * Handle cart update event
   */
  #handleCartUpdate = () => {
    this.#checkAndReload();
  };


  /**
   * Setup observer to detect when grid is replaced by morphing
   */
  #setupGridObserver() {
    const grid = this.querySelector('[ref="recommendationsGrid"]');
    if (!grid) return;

    this.#gridObserver = new MutationObserver(() => {
      // Check if the grid was replaced with skeleton
      const hasSkeleton = grid.querySelector('.cart-recommendations__skeleton');
      const hasCards = grid.querySelectorAll('.cart-recommendation-card').length > 0;

      if (hasSkeleton && !hasCards && this.#lastRenderedProducts) {
        this.#renderRecommendations(this.#lastRenderedProducts, this.#lastRenderedCartIds);
      }
    });

    this.#gridObserver.observe(grid, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Check if cart changed and reload if needed
   */
  async #checkAndReload() {
    const cartData = await this.#getCartData();

    if (!cartData || !cartData.items) return;

    const productIds = cartData.items
      .map(item => Number(item.product_id))
      .filter(id => id && !isNaN(id));

    const currentCartState = productIds.sort().join(',');

    // Force reload if transitioning from empty (no previous state) to non-empty
    const wasEmpty = this.#lastCartState === null || this.#lastCartState === '';
    const isNowFilled = currentCartState !== '';

    // Only reload if cart state actually changed OR if transitioning from empty to filled
    if (this.#lastCartState !== currentCartState || (wasEmpty && isNowFilled)) {
      this.#loadRecommendations();
    }
  }

  /**
   * Get cart data from Shopify Cart API
   * @returns {Promise<Object>}
   */
  async #getCartData() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error('Failed to fetch cart data');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart data:', error);
      return null;
    }
  }

  /**
   * Load product recommendations based on cart items
   */
  async #loadRecommendations() {
    if (this.#isLoading) return;

    this.#isLoading = true;

    try {
      const cartData = await this.#getCartData();

      // If cart is empty or no data, hide recommendations
      if (!cartData || !cartData.items || cartData.items.length === 0) {
        this.classList.add('hidden');
        this.#isLoading = false;
        return;
      }

      // Extract product IDs from cart items (ensure they are numbers)
      const productIds = cartData.items
        .map(item => Number(item.product_id))
        .filter(id => id && !isNaN(id));

      if (productIds.length === 0) {
        this.classList.add('hidden');
        this.#isLoading = false;
        return;
      }

      // Check if cart state has actually changed
      const currentCartState = productIds.sort().join(',');
      if (this.#hasLoaded && this.#lastCartState === currentCartState) {
        // Cart hasn't changed, don't reload
        this.#isLoading = false;
        return;
      }

      // Update cart state
      this.#lastCartState = currentCartState;

      this.classList.remove('hidden');

      // Fetch recommendations for multiple products and merge them
      const allRecommendations = [];
      const seenProductIds = new Set(productIds); // Track products already in cart

      // Only fetch from first product for speed
      const primaryProductId = productIds[0];

      const recommendations = await this.#fetchRecommendations(primaryProductId);

      if (recommendations && recommendations.products && recommendations.products.length > 0) {
        // Add unique recommendations
        recommendations.products.forEach(product => {
          const numProductId = Number(product.id);
          if (!seenProductIds.has(numProductId)) {
            allRecommendations.push(product);
            seenProductIds.add(numProductId);
          }
        });
      }

      // Show immediately if we have at least 3
      if (allRecommendations.length >= 4) {
        this.#renderRecommendations(allRecommendations, productIds);
        this.#hasLoaded = true;
        this.#retryCount = 0;
        this.#isLoading = false;
        return;
      }

      // If we need more, fetch from second product
      if (allRecommendations.length < 4 && productIds.length > 1) {
        const secondRecommendations = await this.#fetchRecommendations(productIds[1]);

        if (secondRecommendations && secondRecommendations.products) {
          secondRecommendations.products.forEach(product => {
            const numProductId = Number(product.id);
            if (!seenProductIds.has(numProductId) && allRecommendations.length < 6) {
              allRecommendations.push(product);
              seenProductIds.add(numProductId);
            }
          });
        }
      }

      if (allRecommendations.length > 0) {
        this.#renderRecommendations(allRecommendations, productIds);
        this.#hasLoaded = true;
        this.#retryCount = 0; // Reset retry count on success
      } else {
        // No recommendations found - try retry if available
        if (this.#retryCount < this.#maxRetries) {
          this.#retryCount++;
          this.#isLoading = false;
          // Retry after a short delay
          setTimeout(() => {
            this.#loadRecommendations();
          }, 500);
          return;
        }
        this.classList.add('hidden');
      }
    } catch (error) {
      console.error('Error loading cart recommendations:', error);

      // Retry on error if retries available
      if (this.#retryCount < this.#maxRetries) {
        this.#retryCount++;
        this.#isLoading = false;
        setTimeout(() => {
          this.#loadRecommendations();
        }, 500);
        return;
      }

      this.classList.add('hidden');
    } finally {
      this.#isLoading = false;
    }
  }

  /**
   * Fetch product recommendations from Shopify
   * @param {string} productId - The product ID to get recommendations for
   * @returns {Promise<Object | null>}
   */
  async #fetchRecommendations(productId) {
    if (!productId) {
      return null;
    }

    const cacheKey = productId;

    // Return cached recommendations if available
    if (this.#cachedRecommendations[cacheKey]) {
      return this.#cachedRecommendations[cacheKey];
    }

    // Build the URL for fetching recommendations
    const recommendationsUrl = `/recommendations/products.json?product_id=${productId}&limit=4`;

    // Cancel any pending fetch
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(recommendationsUrl, {
        signal: this.#activeFetch.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Cache the result
      this.#cachedRecommendations[cacheKey] = data;

      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      throw error;
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Render recommendations into the grid
   * @param {Array} products - Array of recommended products
   * @param {number[]} cartProductIds - Product IDs currently in the cart
   */
  #renderRecommendations(products, cartProductIds) {
    // Cache for re-rendering after morphing
    this.#lastRenderedProducts = products;
    this.#lastRenderedCartIds = cartProductIds;

    const grid = this.querySelector('[ref="recommendationsGrid"]');
    if (!grid) return;

    // Clear skeleton
    grid.innerHTML = '';

    // Convert cart product IDs to a Set of numbers for efficient lookup
    const cartIdSet = new Set(cartProductIds.map(id => Number(id)));

    // Filter out products already in cart and limit to 3
    const filteredProducts = products
      .filter(product => {
        const productId = Number(product.id);
        return !cartIdSet.has(productId);
      })
      .slice(0, 4);

    if (filteredProducts.length === 0) {
      this.classList.add('hidden');
      return;
    }

    // Render each product card
    filteredProducts.forEach(product => {
      const cardHtml = this.#createProductCard(product);
      if (cardHtml) {
        grid.insertAdjacentHTML('beforeend', cardHtml);
      }
    });

    // Attach event listeners to add-to-cart forms
    this.#attachFormListeners();

    // Force show the component
    this.classList.remove('hidden');
  }

  /**
   * Create a product card HTML
   * @param {Object} product - The product object from recommendations API
   * @returns {string}
   */
  #createProductCard(product) {
    try {
      const variant = product.variants && product.variants.length > 0 ? product.variants[0] : null;

      if (!variant) return '';

      const productUrl = `/products/${product.handle}`;
      const imageSrc = product.featured_image || '';
      const imageAlt = product.title || '';
      const moneyFormat = this.getAttribute('data-money-format');
      const price = formatCurrency(variant.price, moneyFormat);
      const compareAtPrice = variant.compare_at_price > variant.price ? formatCurrency(variant.compare_at_price, moneyFormat) : null;
      const available = variant.available;

      return `
        <div class="cart-recommendation-card" data-product-id="${product.id}">
          <a href="${productUrl}" class="cart-recommendation-card__link">
            <div class="cart-recommendation-card__image-wrapper">
              ${imageSrc ? `<img src="${imageSrc}" alt="${this.#escapeHtml(imageAlt)}" class="cart-recommendation-card__image" loading="lazy">` : '<div class="cart-recommendation-card__placeholder"></div>'}
            </div>
            <div class="cart-recommendation-card__details">
              <h4 class="cart-recommendation-card__title">${this.#escapeHtml(product.title)}</h4>
              <div class="cart-recommendation-card__price-wrapper">
                ${compareAtPrice ? `
                  <span class="cart-recommendation-card__price cart-recommendation-card__price--sale money">${price}</span>
                  <s class="cart-recommendation-card__compare-price money">${compareAtPrice}</s>
                ` : `
                  <span class="cart-recommendation-card__price money">${price}</span>
                `}
              </div>
            </div>
          </a>
          <form class="cart-recommendation-card__form" action="/cart/add" method="post" data-variant-id="${variant.id}">
            <input type="hidden" name="id" value="${variant.id}">
            <input type="hidden" name="quantity" value="1">
            <button type="submit" class="cart-recommendation-card__add-button button button--small" ${!available ? 'disabled' : ''}>
              ${available ? '<span class="cart-recommendation-card__add-icon">Add to cart</span>' : '<span class="cart-recommendation-card__sold-out">Sold Out</span>'}
            </button>
          </form>
        </div>
      `;
    } catch (error) {
      console.error('Error creating product card:', error);
      return '';
    }
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string}
   */
  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Attach event listeners to add-to-cart forms
   */
  #attachFormListeners() {
    const forms = this.querySelectorAll('.cart-recommendation-card__form');

    forms.forEach((form) => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.#handleAddToCart(form);
      });
    });
  }

  /**
   * Handle add to cart submission
   * @param {HTMLFormElement} form - The form element
   */
  async #handleAddToCart(form) {
    const button = form.querySelector('button');
    const originalContent = button.innerHTML;
    const variantId = form.querySelector('input[name="id"]')?.value;

    // Disable button and show loading state
    button.disabled = true;
    button.innerHTML = '<span>Adding...</span>';

    try {
      const formData = new FormData(form);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      const itemData = await response.json();

      // Fetch updated cart data to get accurate item count
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      // Fetch updated cart sections to refresh the drawer
      const sectionsToRender = ['cart-drawer', 'cart-icon-bubble'];
      const sectionsResponse = await fetch(`${window.location.pathname}?sections=${sectionsToRender.join(',')}`);
      const sections = await sectionsResponse.json();

      // Success - update button temporarily
      button.innerHTML = '<span>✓</span>';

      // Dispatch the proper cart update event to trigger drawer refresh
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: itemData,
            sourceId: 'cart-recommendation',
            data: {
              sections: sections,
              itemCount: cartData.item_count,
              variantId: variantId,
              source: 'recommendation'
            }
          }
        })
      );

      // Reset button after delay
      setTimeout(() => {
        button.innerHTML = originalContent;
        button.disabled = false;
      }, 1500);

    } catch (error) {
      console.error('Error adding to cart:', error);
      button.innerHTML = originalContent;
      button.disabled = false;

      // Show error to user
      alert(error.message || 'Failed to add product to cart. Please try again.');
    }
  }
}

if (!customElements.get('cart-product-recommendations')) {
  customElements.define('cart-product-recommendations', CartProductRecommendations);
}
